"use client";

import type { GameData } from "@/app/games/(details)/types";
import { Action } from "@/context/AnalyticsProvider";
import { useGamePageOldAnalytics } from "@/context/AnalyticsProvider/gamePageOld";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import ReactPlayer from "react-player-v3";

interface VideoIntroProps {
  videoSrc?: string;
  accentColour: string | undefined;
  children: React.ReactNode;
  gameData: GameData;
  introVideoExperimentNov25Gpt: boolean | undefined;
}

export const VideoIntro = ({
  videoSrc,
  accentColour,
  children,
  gameData,
  introVideoExperimentNov25Gpt,
}: VideoIntroProps) => {
  const { track } = useGamePageOldAnalytics();
  const [videoEnded, setVideoEnded] = useState(
    introVideoExperimentNov25Gpt === false,
  );
  const [showOverlay, setShowOverlay] = useState(
    introVideoExperimentNov25Gpt === false,
  );
  const [isBuffering, setIsBuffering] = useState(
    !!introVideoExperimentNov25Gpt,
  );
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!isBuffering && !!introVideoExperimentNov25Gpt)
      setTimeout(() => setIsPlaying(true), 150);
  }, [isBuffering, introVideoExperimentNov25Gpt]);

  useEffect(() => {
    // add overflow hidden to the body
    if (introVideoExperimentNov25Gpt) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [introVideoExperimentNov25Gpt]);

  useEffect(() => {
    document.body.style.setProperty(
      "--accent-colour",
      accentColour?.replace("rgb(", "").replace(")", "") || "81,255,0",
    );
  }, [accentColour]);

  const handleOnLoaded = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      track({
        action: Action.VIEWED,
        extras: {
          id: "video-intro-start",
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
          version: "v3",
        },
      });
      if (event.currentTarget.buffered.length >= 1) setIsBuffering(false);
    },
    [gameData.details.game_id, gameData.details.name, track],
  );

  const handleVideoEnd = useCallback(() => {
    setIsPlaying(false);
    track({
      action: Action.VIEWED,
      extras: {
        id: "video-intro-end",
        gameId: gameData.details.game_id,
        gameName: gameData.details.name,
        version: "v3",
      },
    });

    // First fade in the overlay
    setShowOverlay(true);

    // Then show the page content after a brief delay
    setTimeout(() => {
      setVideoEnded(true);
      setTimeout(() => {
        document.body.style.overflow = "";
      }, 700);
    }, 700); // Adjust timing as needed
  }, [gameData.details.game_id, gameData.details.name, track]);

  if (!videoSrc) return children;

  return (
    <>
      {/* Video - hide once ended */}
      <ReactPlayer
        src={videoSrc}
        playing={isPlaying}
        muted
        loop={false}
        width="100%"
        height="100%"
        onProgress={handleOnLoaded}
        onEnded={handleVideoEnd}
        className={cn(
          "fixed inset-0 w-screen min-h-lvh object-cover [--media-object-fit:cover]",
          showOverlay ? "z-0" : "z-10",
        )}
        controls={false}
        playsInline
      />

      {/* Loading indicator - show while video is buffering */}
      <div
        className={cn(
          "w-screen h-screen bg-neutral-900 z-10 fixed pointer-events-none inset-0 flex items-center justify-center duration-500 transition-all",
          isBuffering ? "opacity-100 scale-100" : "opacity-0 scale-[10]",
        )}
      >
        <Image
          src="/images/immutable-loader.webp"
          alt="Loading"
          width={220}
          height={220}
          className="animate-pulse"
          priority
        />
      </div>

      {/* Overlay - fade in when video ends  */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 transition-opacity duration-500",
          showOverlay ? "opacity-100" : "opacity-0",
        )}
      />

      {/* Page Content - render after overlay shows, animate after overlay fades in  */}
      {showOverlay ? (
        <div
          className={cn(
            "flex w-full h-full transition-all duration-700",
            videoEnded
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10 pointer-events-none",
          )}
        >
          {children}
        </div>
      ) : null}
    </>
  );
};
